<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  reactive,
  ref,
  toRefs,
  unref,
  watch
} from "vue";
import { getLabel, convertInternshipFilterCriteria } from "../../utils";
import { PencilIcon } from "@heroicons/vue/24/outline";
import TrashCan from "@/components/shared/icons/TrashCan.vue";
import { useGetInternships } from "@/components/program-manager/sessions/composable";
import { miniTableColumns } from "@/components/program-manager/sessions/components/Internships/constants";
import Pagination from "@/components/shared/Pagination.vue";
import { FlexRender } from "@tanstack/vue-table";
import Spinner from "@/components/helpers/Spinner.vue";
import { differenceWith, isEqual } from "lodash";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useVueTable,
} from "@tanstack/vue-table";
import { createTestId } from "@/util/string.js";
import { useGetManyInternships } from "../../composable";

const props = defineProps({
  filters: {
    type: Object,
    required: false,
  },
  academicStartDate: {
    type: String,
    required: false,
    default: null,
  },
  selectedInternshipIds: {
    type: Array,
    default: () => [],
  },
});

const { filters, academicStartDate, selectedInternshipIds } = toRefs(props);

const emit = defineEmits(["editFilters", "clearFilters", "updateDefaultId"]);

const onClear = () => {
  emit("clearFilters", true);
};
const onEdit = () => {
  emit("editFilters", true);
};

const internshipParams = ref(
  convertInternshipFilterCriteria({ ...unref(filters) })
);

const {
  isLoading: criteriaInternshipsDataLoading,
  execute: fetchInternshipsData,
  state: criteriaInternshipsData,
  isReady: criteriaInternshipsIsReady,
} = useGetInternships(
  { immediate: true, throwError: false, resetOnExecute: true  },
  {
    ...unref(internshipParams),
    limit: 10,
    skip: 0,
  }
);

const {
  isLoading: selectedInternshipsDataLoading,
  execute: fetchSelectedInternshipsData,
  state: selectedInternshipsData,
  isReady: selectedInternshipsIsReady,
} = useGetManyInternships(
  { immediate: true, throwError: false },
  {internship_ids: unref(selectedInternshipIds)}
);

const internshipsDataLoading = computed(() => {
  return !criteriaInternshipsDataLoading && !selectedInternshipsDataLoading;
});

const internshipsIsReady = computed(() => {
  return criteriaInternshipsIsReady && selectedInternshipsIsReady;
});

const internshipsData = computed(() => (Boolean(selectedInternshipIds?.value?.length) ? selectedInternshipsData.value : criteriaInternshipsData.value) ?? []);

const filterCities = computed(() => filters?.cities ?? []);

const pagination = reactive({
  pageIndex: 1,
  pageSize: 10,
});

const columnHelper = createColumnHelper();
const columns = miniTableColumns.map(
  ({ header, id, size, type, addedClasses, getColumnDisplayValue }) => {
    return columnHelper.accessor(getColumnDisplayValue, {
      id,
      header,
      size,
      type,
      addedClasses,
    });
  }
);

const table = useVueTable({
  get data() {
    return internshipsData?.value?.data?.items || [];
  },
  enableColumnResizing: true,
  columns: columns,
  columnResizeMode: "onChange",
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  manualPagination: true,
  meta: { academicStartDate: unref(academicStartDate) },
});

const transformPlacementType = item => ({
    id: item.id,
    label: item.value
  });

const placementTypes = computed(() => {
  const placement_type = props?.filters?.placement_type;

  if (!placement_type) {
    return [];
  }

  return Array.isArray(placement_type)
    ? placement_type.map(transformPlacementType)
    : [transformPlacementType(placement_type)];
});

const totalRecords = computed(() => {
  const newDefaultId =
    Array.isArray(internshipsData?.value?.data?.items) &&
    internshipsData?.value?.data?.items[0]?.id;
  if (!unref(filters)?.default_internship_id) {
    emit("updateDefaultId", newDefaultId);
  }
  return internshipsData?.value?.data?.count &&
    internshipsData?.value?.data?.count !== -1
    ? internshipsData.value.data.count
    : 0;
});

const changePage = (pageIndex) => {
  pagination.pageIndex = pageIndex;
  fetchInternshipsData(0, {
    ...unref(internshipParams),
    limit: pagination.pageSize,
    skip: pagination.pageSize * (pagination.pageIndex - 1),
  });
};

watch(
  [
    () => props.filters?.durations,
    () => props.filters?.cities,
    () => props.filters?.terms,
    () => props.filters?.work_locations,
    () => props.filters?.placement_type,
  ],
  (
    [newDurations, newCities, newTerms, newWorkLocations, newPlacementType],
    [_oldDurations, _oldCities, oldTerms, _oldWorkLocations, _oldPlacementType]
  ) => {
    const results = differenceWith(newTerms || [], oldTerms || [], isEqual);
    if (results.length) {
      emit("updateDefaultId", undefined);
    }
    internshipParams.value = convertInternshipFilterCriteria({
      durations: newDurations,
      cities: newCities,
      terms: newTerms,
      work_locations: newWorkLocations,
      placement_type: newPlacementType,
    });
  },
  { deep: true }
);

watch(selectedInternshipIds, () => {
  internshipsData.value = undefined;
  fetchSelectedInternshipsData();
});

const setInternshipDefault = (id) => {
  emit("updateDefaultId", id);
};

const isDefaultInternship = (id) => {
  return unref(filters)?.default_internship_id === id;
};
</script>

<template>
  <div class="flex flex-col col-span-2">
    <div class="block text-xs font-semibold text-gray-700">From Library</div>
    <div class="grid grid-cols-10 gap-x-2 gap-y-4 items-center bg-white border-gray-300 rounded-md w-full p-4 mt-2">
      <div class="min-h-8 flex flex-wrap items-center col-span-9">
        <template v-if="filters?.terms">
          <div v-for="term in  filters.terms " :key="term?.id" :data-testid="createTestId(term.name, 'term-badge')"
            class="inline-flex items-center rounded-full bg-blue-400 mb-1 mr-2 py-0.5 px-2.5 text-sm font-medium text-purple-100">
            {{ term.name }}
          </div>
        </template>
        <div v-for=" city  in  filterCities" :key="city?.id"
             :data-testid="createTestId(city?.city_ascii, 'city-badge')"
             class="inline-flex items-center rounded-full bg-blue-400 mb-1 mr-2 py-0.5 px-2.5 text-sm font-medium text-purple-100">
          {{ city?.city_ascii || "" }}
        </div>
        <div v-if="filters?.durations"
          class="inline-flex items-center rounded-full bg-blue-400 mb-1 mr-2 py-0.5 px-2.5 text-sm font-medium text-purple-100">
          {{ getLabel(filters.durations) }}
        </div>
        <!--        <div-->
        <!--          v-for="duration in props.filters.durations"-->
        <!--          :id="duration"-->
        <!--          class="inline-flex items-center rounded-full bg-blue-400 mb-1 mr-2 py-0.5 px-2.5 text-sm font-medium text-purple-100"-->
        <!--        >-->
        <!--          {{ getLabel(duration) }}-->
        <!--        </div>-->
        <div v-for=" placementType  in  placementTypes "
          :data-testid="createTestId(getLabel(placementType), 'placement-type-badge')"
          class="inline-flex items-center rounded-full bg-blue-400 mb-1 mr-2 py-0.5 px-2.5 text-sm font-medium text-purple-100">

          {{ getLabel(placementType) }}
        </div>
      </div>
      <div class="flex w-[48px] justify-center align-center ml-auto">
        <pencil-icon data-testid="edit-internship-filter-criteria" class="w-[18px] mr-3 cursor-pointer"
          @click="onEdit" />
        <TrashCan data-testid="delete-internship-filter-criteria" class="w-[18px] m-auto cursor-pointer"
          @click="onClear" />
      </div>
      <div class="col-span-10">
        <table class="block">
          <thead>
            <tr v-for=" headerGroup  in  table.getHeaderGroups() " :key="headerGroup.id">
              <th v-for=" header  in  headerGroup.headers " :key="header.id" :colSpan="header.colSpan"
                :style="{ width: `${header.getSize()}px` }" class="py-4 text-left text-sm items-center text-gray-900">
                <div class="flex items-center" :class="header.column.columnDef.addedClasses">
                  <FlexRender v-if="!header.isPlaceholder" :render="header.column.columnDef.header"
                    :props="header.getContext()" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr v-for=" row  in table.getRowModel().rows" :key="row.id" class="hover:bg-blue-350">
              <td v-for=" cell  in row.getVisibleCells()" :key="cell.column.id"
                :style="{ width: `${cell.column.getSize()}px` }" class="py-2 text-sm first:text-teal-900 capitalize"
                :class="cell.column.columnDef.addedClasses">
                <div v-if="cell.column.columnDef.type === 'default_checkbox'">
                  <input type="radio" :value="isDefaultInternship(cell.row.original.id)"
                    :checked="isDefaultInternship(cell.row.original.id)" class="h-4 w-4 border-gray-300 form-radio"
                    @input="() => setInternshipDefault(cell.row.original.id)" />
                </div>
                <div v-if="cell.column.columnDef.header === 'Title'">
                  <a class="text-teal-900" :href="`/program-manager/internships/item-${row.original.id}`"
                    target="_blank">{{
          row?.original?.title || "" }}</a>
                </div>
                <FlexRender v-else :render="cell.column.columnDef.cell" :props="cell.getContext()" />
              </td>
            </tr>
          </tbody>
          <Spinner v-show="internshipsDataLoading || !internshipsIsReady"
            outer-classes="flex items-center justify-center bg-white h-full relative" />
        </table>
        <Pagination
          outer-classes="flex items-center justify-between border-t border-gray-200 pt-4"
          :page="pagination.pageIndex"
          :total-of-records="totalRecords"
          :records-per-page="pagination.pageSize"
          @changePage="changePage"
        />
      </div>
    </div>
  </div>
</template>